import React, { useState, useEffect } from "react";
import { formatCountFit } from "../../../../utils/numberFormat";
import { fetchData } from "../../../../utils/functions";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import NoData, { hasData } from "../../../common/NoData";
import DashboardBarChart from "../../../common/DashboardBarChart";
import { BRAND, getCategoryColors } from "../../../../utils/chartTheme";

const hasSeries = (chartData) =>
  hasData((chartData?.data || []).map((item) => item.count));

const HousingTab = ({ filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState({
    tenureStatus: { data: [], total: 0 },
    roofMaterial: { data: [], total: 0 },
    wallMaterial: { data: [], total: 0 },
    floorType: { data: [], total: 0 },
    roomCount: { data: [], total: 0 },
    waterSource: { data: [], total: 0 },
    waterTime: { data: [], total: 0, avg: 0 },
    waterCost: { data: [], total: 0 },
    garbageDisposal: { data: [], total: 0 },
    toiletType: { data: [], total: 0 },
    lightingSource: { data: [], total: 0 },
    cookingFuel: { data: [], total: 0 },
    separateKitchen: { data: [], total: 0 },
    lightingCost: { data: [], total: 0 },
    cookingCost: { data: [], total: 0 },
    organicWaste: { data: [], total: 0 },
  });

  const [loading, setLoading] = useState(true);

  const formatCount = (num) =>
    num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0";

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    } else {
      return num.toLocaleString();
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [
          tenureRes,
          roofRes,
          wallRes,
          floorRes,
          roomRes,
          waterSourceRes,
          waterTimeRes,
          waterCostRes,
          garbageRes,
          toiletRes,
          lightingRes,
          cookingFuelRes,
          kitchenRes,
          lightingCostRes,
          cookingCostRes,
          organicWasteRes,
        ] = await Promise.all([
          fetchData(
            "housing-tenure-status",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-tenure-status",
            dispatch
          ),
          fetchData(
            "housing-roof-material",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-roof-material",
            dispatch
          ),
          fetchData(
            "housing-wall-material",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-wall-material",
            dispatch
          ),
          fetchData(
            "housing-floor-type",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-floor-type",
            dispatch
          ),
          fetchData(
            "housing-room-count",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-room-count",
            dispatch
          ),
          fetchData(
            "housing-water-source",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-water-source",
            dispatch
          ),
          fetchData(
            "housing-water-time",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-water-time",
            dispatch
          ),
          fetchData(
            "housing-water-cost",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-water-cost",
            dispatch
          ),
          fetchData(
            "housing-garbage-disposal",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-garbage-disposal",
            dispatch
          ),
          fetchData(
            "housing-toilet-type",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-toilet-type",
            dispatch
          ),
          fetchData(
            "housing-lighting-source",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-lighting-source",
            dispatch
          ),
          fetchData(
            "housing-cooking-fuel",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-cooking-fuel",
            dispatch
          ),
          fetchData(
            "housing-separate-kitchen",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-separate-kitchen",
            dispatch
          ),
          fetchData(
            "housing-lighting-cost",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-lighting-cost",
            dispatch
          ),
          fetchData(
            "housing-cooking-cost",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-cooking-cost",
            dispatch
          ),
          fetchData(
            "housing-organic-waste",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/housing-organic-waste",
            dispatch
          ),
        ]);

        setData({
          tenureStatus: tenureRes || { data: [], total: 0 },
          roofMaterial: roofRes || { data: [], total: 0 },
          wallMaterial: wallRes || { data: [], total: 0 },
          floorType: floorRes || { data: [], total: 0 },
          roomCount: roomRes || { data: [], total: 0 },
          waterSource: waterSourceRes || { data: [], total: 0 },
          waterTime: waterTimeRes || { data: [], total: 0, avg: 0 },
          waterCost: waterCostRes || { data: [], total: 0 },
          garbageDisposal: garbageRes || { data: [], total: 0 },
          toiletType: toiletRes || { data: [], total: 0 },
          lightingSource: lightingRes || { data: [], total: 0 },
          cookingFuel: cookingFuelRes || { data: [], total: 0 },
          separateKitchen: kitchenRes || { data: [], total: 0 },
          lightingCost: lightingCostRes || { data: [], total: 0 },
          cookingCost: cookingCostRes || { data: [], total: 0 },
          organicWaste: organicWasteRes || { data: [], total: 0 },
        });
      } catch (err) {
        console.error("Error loading Housing tab:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, navigate, dispatch]);

  // pie chart options
  const createPieChartOptions = (labels) => ({
    chart: {
      type: "pie",
      height: 280,
    },
    plotOptions: { pie: { dataLabels: { minAngleToShowLabel: 18 } } },
    labels: labels,
    colors: getCategoryColors(labels),
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "11px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      formatter: (val) => `${val.toFixed(1)}%`,
      dropShadow: {
        enabled: false,
      },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "10px",
      horizontalAlign: "center",
      offsetY: 0,
      markers: {
        width: 10,
        height: 10,
      },
    },
    tooltip: {
      enabled: false,
    },
  });

  // donut chart options
  const createDonutChartOptions = (labels) => ({
    chart: {
      type: "donut",
      height: 280,
    },
    labels: labels,
    colors: getCategoryColors(labels),
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "11px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      formatter: (val) => `${val.toFixed(1)}%`,
      dropShadow: {
        enabled: false,
      },
    },
    plotOptions: {
      pie: {
        dataLabels: { minAngleToShowLabel: 18 },
        donut: {
          size: "65%",
          labels: {
            show: true,
            value: { fontSize: "18px", fontWeight: 700, formatter: (val) => formatCountFit(val) },
            total: {
              show: true,
              label: "Total",
              formatter: (w) => formatCountFit(w.globals.seriesTotals.reduce((a, b) => a + b, 0)),
              fontSize: "14px",
              fontWeight: 600,
              color: "#374151",
            },
          },
        },
      },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "10px",
      horizontalAlign: "center",
      offsetY: 0,
      markers: {
        width: 10,
        height: 10,
      },
    },
    tooltip: {
      enabled: false,
    },
  });

  // area chart options
  const createAreaChartOptions = (categories, chartType = "default") => {
    return {
      chart: {
        type: "area",
        height: 280,
        toolbar: { show: false },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
      },
      colors: [BRAND.teal],
      dataLabels: {
        enabled: true,
        dataLabels: {
          position: "center",
        },

        style: {
          fontSize: "10px",
          fontWeight: 700,
          // ApexCharts fills the badge with the label colour and uses foreColor for the text:
          // white badge, dark text, like every other chart.
          colors: ["#ffffff"],
        },
        offsetY: -8,
        background: {
          enabled: true,
          foreColor: "#374151",
          padding: 4,
          borderRadius: 3,
          borderWidth: 1,
          borderColor: "#e5e7eb",
          opacity: 0.95,
        },
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 90, 100],
        },
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            fontSize: "9px",
            fontWeight: 600,
            colors: "#6b7280",
          },
          rotate: -45,
          rotateAlways: categories.length > 4,
          trim: false,
          hideOverlappingLabels: false,
          maxHeight: 500,
        },
        axisBorder: {
          show: true,
          color: "#e5e7eb",
        },
        axisTicks: {
          show: true,
          color: "#e5e7eb",
        },
      },
      yaxis: {
        title: {
          text: "Count",
          style: {
            fontSize: "11px",
            fontWeight: 600,
            color: "#6b7280",
          },
        },
        labels: {
          style: {
            fontSize: "9px",
            colors: "#6b7280",
          },
          formatter: (val) => formatNumber(val),
        },
      },
      grid: {
        borderColor: "#f3f4f6",
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          top: 0,
          right: 8,
          bottom: 0,
          left: 8,
        },
      },
      tooltip: {
        enabled: true,
        theme: "light",
        y: {
          formatter: (val, opts) => {
            const dataItem =
              opts.w.config.series[0].dataWithPercentage[opts.dataPointIndex];
            return `${formatCount(val)} (${dataItem.percentage}%)`;
          },
        },
        style: {
          fontSize: "11px",
        },
      },
      legend: { show: false },
    };
  };

  // Render bar chart section
  const renderChartSection = (
    title,
    chartData,
    chartType,
    subtitle = null
  ) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }
    return (
      <div className="h-full flex flex-col">
        <div className="mb-2 text-center flex-shrink-0">
          {subtitle && <p className="text-xs text-gray-600 mt-1">{subtitle}</p>}
        </div>
        <div className="flex-grow relative">
          <DashboardBarChart items={chartData.data} vertical />
        </div>
      </div>
    );
  };

  // Render pie chart section
  const renderPieChartSection = (title, chartData, subtitle = null) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }

    const labels = chartData.data.map((item) => item.name);
    const values = chartData.data.map((item) => item.count);
    const chartOptions = createPieChartOptions(labels);

    return (
      <div className="h-full flex flex-col">
        <div className="mb-2 text-center flex-shrink-0">
          {subtitle && <p className="text-xs text-gray-600 mt-1">{subtitle}</p>}
        </div>
        <div className="flex-grow">
          <ReactApexChart
            options={chartOptions}
            series={values}
            type="pie"
            height={280}
          />
        </div>
      </div>
    );
  };

  // Render donut chart section
  const renderDonutChartSection = (title, chartData, subtitle = null) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }

    const labels = chartData.data.map((item) => item.name);
    const values = chartData.data.map((item) => item.count);
    const chartOptions = createDonutChartOptions(labels);

    return (
      <div className="h-full flex flex-col">
        <div className="mb-2 text-center flex-shrink-0">
          {subtitle && <p className="text-xs text-gray-600 mt-1">{subtitle}</p>}
        </div>
        <div className="flex-grow">
          <ReactApexChart
            options={chartOptions}
            series={values}
            type="donut"
            height={280}
          />
        </div>
      </div>
    );
  };

  // Render area chart section
  const renderAreaChartSection = (
    title,
    chartData,
    chartType = "default",
    subtitle = null
  ) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }

    const categories = chartData.data.map((item) => item.name);
    const values = chartData.data.map((item) => item.count);
    const chartOptions = createAreaChartOptions(categories, chartType);

    const seriesWithPercentage = [
      {
        name: "Count",
        data: values,
        dataWithPercentage: chartData.data.map((item) => ({
          count: item.count,
          percentage: item.percentage,
        })),
      },
    ];

    return (
      <div className="h-full flex flex-col">
        <div className="mb-2 text-center flex-shrink-0">
          {subtitle && <p className="text-xs text-gray-600 mt-1">{subtitle}</p>}
        </div>
        <div className="flex-grow">
          <ReactApexChart
            options={chartOptions}
            series={seriesWithPercentage}
            type="area"
            height={280}
          />
        </div>
      </div>
    );
  };

  // Render column chart section
  const renderColumnChartSection = (
    title,
    chartData,
    chartType,
    subtitle = null
  ) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }
    return (
      <div className="h-full flex flex-col">
        <div className="mb-2 text-center flex-shrink-0">
          {subtitle && <p className="text-xs text-gray-600 mt-1">{subtitle}</p>}
        </div>
        <div className="flex-grow relative">
          <DashboardBarChart items={chartData.data} vertical />
        </div>
      </div>
    );
  };

  // Render horizontal bar chart section
  const renderHorizontalBarSection = (title, chartData, chartType, subtitle = null) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }
    return (
      <div className="h-full flex flex-col">
        <div className="mb-2 text-center flex-shrink-0">
          {subtitle && <p className="text-xs text-gray-600 mt-1">{subtitle}</p>}
        </div>
        <div className="flex-grow">
          <DashboardBarChart horizontal items={chartData.data} allowList={false} />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#137E81]"></div>
        <p className="mt-4 text-gray-600">Loading housing data...</p>
      </div>
    );
  }

  const anyData = Object.keys(data).some((k) => hasSeries(data[k]));
  const rowHas = () => anyData;

  return (
    <div className="space-y-6 pb-8">
      {!Object.keys(data).some((k) => hasSeries(data[k])) && (
        <NoData className="py-16" />
      )}
      {/* Row 1: Housing Structure */}
      {rowHas("tenureStatus", "roofMaterial", "wallMaterial") && (<>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenure Status - Bar Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Tenure Status
          </h3>
          <div className="flex-grow">
            {renderChartSection("Tenure Status", data.tenureStatus, "tenure")}
          </div>
        </div>

        {/* Roof Material - Donut Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Material Used for Roof
          </h3>
          <div className="flex-grow">
            {renderDonutChartSection("Roof Material", data.roofMaterial)}
          </div>
        </div>

        {/* Wall Material - Horizontal Bar Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Material Used for Walls
          </h3>
          <div className="flex-grow">
            {renderHorizontalBarSection(
              "Wall Material",
              data.wallMaterial,
              "wall"
            )}
          </div>
        </div>
      </div>
      </>)}

      {/* Row 2: Floor, Rooms, and Kitchen */}
      {rowHas("floorType", "roomCount", "separateKitchen") && (<>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Floor Type - Pie Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Type of Floor
          </h3>
          <div className="flex-grow">
            {renderPieChartSection("Floor Type", data.floorType)}
          </div>
        </div>

        {/* Number of Rooms - Column Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Number of Rooms
          </h3>
          <div className="flex-grow">
            {renderColumnChartSection("Room Count", data.roomCount, "room")}
          </div>
        </div>

        {/* Separate Kitchen - Semi Circle Gauge */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Separate Kitchen
          </h3>
          <div className="flex-grow">
            {renderDonutChartSection("Separate Kitchen", data.separateKitchen)}
          </div>
        </div>
      </div>
      </>)}

      {/* Row 3: Water Services - 3 columns */}
      {rowHas("waterSource", "waterTime", "waterCost") && (<>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Water Source - Bar Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Source of Drinking Water
          </h3>
          <div className="flex-grow">
            {renderChartSection(
              "Water Source",
              data.waterSource,
              "waterSource"
            )}
          </div>
        </div>

        {/* Water Time - Horizontal Bar Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Water Fetching Time
          </h3>
          <div className="flex-grow">
            {data.waterTime.avg > 0 && (
              <div className="mb-2 text-center">
                <span className="text-xs font-semibold text-[#3A3A3A]">
                  Average: {data.waterTime.avg} minutes
                </span>
              </div>
            )}
            {renderHorizontalBarSection(
              "Water Time",
              data.waterTime,
              "water",
              "(Round Trip)"
            )}
          </div>
        </div>

        {/* Water Cost - Area Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Water Fetching Cost
          </h3>
          <div className="flex-grow">
            {renderAreaChartSection(
              "Water Cost",
              data.waterCost,
              "water",
              "(per month)"
            )}
          </div>
        </div>
      </div>
      </>)}

      {/* Row 4: Waste Management */}
      {rowHas("garbageDisposal", "organicWaste", "toiletType") && (<>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Garbage Disposal - Column Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Garbage Disposal Methods
          </h3>
          <div className="flex-grow">
            {renderColumnChartSection(
              "Garbage Disposal",
              data.garbageDisposal,
              "garbage"
            )}
          </div>
        </div>

        {/* Organic Waste - Donut Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Organic Waste Separation
          </h3>
          <div className="flex-grow">
            {renderDonutChartSection(
              "Organic Waste",
              data.organicWaste,
              "Segregation practices"
            )}
          </div>
        </div>

        {/* Toilet Type - Horizontal Bar Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Type of Toilets
          </h3>
          <div className="flex-grow">
            {renderHorizontalBarSection(
              "Toilet Type",
              data.toiletType,
              "toilet"
            )}
          </div>
        </div>
      </div>
      </>)}

      {/* Row 5: Lighting Services */}
      {rowHas("lightingSource", "lightingCost", "cookingFuel") && (<>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lighting Source - Bar Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Fuel Used for Lighting
          </h3>
          <div className="flex-grow">
            {renderChartSection(
              "Lighting Source",
              data.lightingSource,
              "lighting"
            )}
          </div>
        </div>

        {/* Lighting Cost - Column Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Electricity Cost by Source
          </h3>
          <div className="flex-grow">
            {renderColumnChartSection(
              "Lighting Cost",
              data.lightingCost,
              "lighting",
              "(per month)"
            )}
          </div>
        </div>

        {/* Cooking Fuel - Donut Chart */}
        <div className="bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Fuel Used for Cooking
          </h3>
          <div className="flex-grow">
            {renderDonutChartSection("Cooking Fuel", data.cookingFuel)}
          </div>
        </div>
      </div>
      </>)}

      {/* Row 6: Cooking Cost - Centered single item */}
      {rowHas("cookingCost") && (<>
      <hr className="border-t-2 border-dashed border-teal-600 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-start-2 bg-white flex flex-col">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Cost of Cooking Fuel
          </h3>
          <div className="flex-grow">
            {renderAreaChartSection(
              "Cooking Cost",
              data.cookingCost,
              "cooking",
              "(per month)"
            )}
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
};

export default HousingTab;
